import * as React from 'react';
import type { Positioner } from 'react-virtualized';
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  Masonry,
  WindowScroller,
  createMasonryCellPositioner,
} from 'react-virtualized';
import type { MeasuredCellParent } from 'react-virtualized/dist/es/CellMeasurer';

type Props = {
  list: { id: string }[];
  renderItem: (index: number) => JSX.Element | null;
  itemWidth: number;
};

type State = {
  columnWidth: number;
  gutterSize: number;
  overscanByPixels: number;
};

const masonryStyles = {
  outline: 'none',
};

export default class VirtualizedMasonry extends React.PureComponent<Props, State> {
  declare _columnCount: number;

  declare _cache: CellMeasurerCache;

  declare _masonry?: Masonry;

  _width = 0;

  declare _cellPositioner: Positioner;

  _height = 0;

  declare _scrollTop?: number;

  declare _scroller?: WindowScroller;

  constructor(props: Props) {
    super(props);
    this.state = {
      columnWidth: props.itemWidth,
      gutterSize: 24,
      overscanByPixels: 0,
    };
    this._cache = new CellMeasurerCache({
      defaultHeight: 250,
      defaultWidth: props.itemWidth,
      fixedWidth: true,
    });
  }

  componentDidUpdate() {
    this._masonry?.forceUpdate();
  }

  render() {
    const { overscanByPixels } = this.state;
    const { list } = this.props;

    return (
      <WindowScroller ref={this._setScrollerRef} overscanByPixels={overscanByPixels} listCount={list.length}>
        {this._renderAutoSizer}
      </WindowScroller>
    );
  }

  _calculateColumnCount() {
    const { columnWidth } = this.state;

    this._columnCount = Math.floor(this._width / columnWidth);
  }

  _cellRenderer = ({
    index,
    key,
    parent,
    style,
  }: {
    index: number;
    key: React.Key;
    parent: MeasuredCellParent;
    style?: React.CSSProperties;
  }) => {
    const { renderItem } = this.props;
    const { columnWidth } = this.state;

    return (
      <CellMeasurer cache={this._cache} index={index} key={key} parent={parent}>
        <div
          style={{
            ...style,
            width: columnWidth,
          }}
        >
          {renderItem(index)}
        </div>
      </CellMeasurer>
    );
  };

  _initCellPositioner() {
    if (typeof this._cellPositioner === 'undefined') {
      const { columnWidth, gutterSize } = this.state;

      this._cellPositioner = createMasonryCellPositioner({
        cellMeasurerCache: this._cache,
        columnCount: this._columnCount,
        columnWidth,
        spacer: gutterSize,
      });
    }
  }

  _onResize = ({ width }: { width: number }) => {
    this._width = width;

    this._calculateColumnCount();
    this._resetCellPositioner();
    this._masonry?.recomputeCellPositions();
  };

  _renderAutoSizer = ({ height, scrollTop }: { height: number; scrollTop: number }) => {
    this._height = height;
    this._scrollTop = scrollTop;

    const { overscanByPixels } = this.state;
    const { list } = this.props;

    return (
      <AutoSizer
        disableHeight
        height={height}
        onResize={this._onResize}
        overscanByPixels={overscanByPixels}
        scrollTop={this._scrollTop}
        listCount={list.length}
      >
        {this._renderMasonry}
      </AutoSizer>
    );
  };

  _renderMasonry = ({ width }: { width: number }) => {
    this._width = width;

    this._calculateColumnCount();
    this._initCellPositioner();

    const { overscanByPixels } = this.state;
    const { list } = this.props;

    return (
      <Masonry
        autoHeight={true}
        cellCount={list.length}
        cellMeasurerCache={this._cache}
        cellPositioner={this._cellPositioner}
        cellRenderer={this._cellRenderer}
        height={this._height}
        overscanByPixels={overscanByPixels}
        ref={this._setMasonryRef}
        scrollTop={this._scrollTop}
        width={width}
        style={masonryStyles}
      />
    );
  };

  _resetList = () => {
    this._cache.clearAll();
    this._resetCellPositioner();
    this._masonry?.clearCellPositions();
  };

  _resetCellPositioner() {
    const { columnWidth, gutterSize } = this.state;

    this._cellPositioner.reset({
      columnCount: this._columnCount,
      columnWidth,
      spacer: gutterSize,
    });
  }

  _setMasonryRef = (ref: Masonry) => {
    this._masonry = ref;
  };

  _setScrollerRef = (ref: WindowScroller) => {
    this._scroller = ref;
  };
}
